// Work Requests, Stage 5 piece 4. Words: WORK_REQUEST_WORDS.md, mockup MOCKUP_WORK_REQUESTS.html
// (approved by Robert 2026-10-11, all six decisions as recommended; workOrderSystem field added 2026-10-11).
//
// One request per JSP, written at Submit when the Field Assessment recorded a finding.
// Every finding is a line with a fixed priority. The request takes the highest.
// Fixed What I Found on the close pill closes it. The ESP Manager screens the rest:
// Sent To CMMS (work order number), Close (resolution), Not Needed (reason).
// Work orders live in the site's CMMS (eMaint today, the EMP later). SafeTag never plans or schedules.
//
// Pure: data in, data out. Shared with the server and unit tested.

import type { JspState } from "./jsp";

export type Priority = "High" | "Medium" | "Low";
export type RequestStatus = "Open" | "In Work" | "Closed";
export type ClosedHow = "Fixed On JSP" | "Closed" | "Not Needed";

export const PRIORITY_RANK: Record<Priority, number> = { High: 3, Medium: 2, Low: 1 };

/** What each priority means. Shown on the request page. */
export const PRIORITY_MEANS: Record<Priority, string> = {
  High: "Look at it today.",
  Medium: "Within a week.",
  Low: "Next planned opportunity.",
};

/** CMMS names offered at Sent To CMMS. eMaint is the Alltech system today. */
export const WORK_ORDER_SYSTEMS = ["eMaint", "EMP", "Other"] as const;
export type WorkOrderSystem = (typeof WORK_ORDER_SYSTEMS)[number];

export interface RequestLine {
  finding: string;
  priority: Priority;
}

/** The Field Assessment answers the lines read. Same shape as the JSP's cond. */
export type FieldCondition = Pick<JspState["cond"], "sense" | "senses" | "doors" | "covers" | "damage" | "dust" | "tamper">;

/** The fixed table (WORK_REQUEST_WORDS.md section 2). Highest first. */
export function linesOf(c: FieldCondition): RequestLine[] {
  const out: RequestLine[] = [];
  if (c.sense && c.senses.length) out.push({ finding: `${c.senses.join(", ")} noticed`, priority: "High" });
  if (c.damage === true) out.push({ finding: "Damage or burn marks", priority: "High" });
  if (c.covers === false) out.push({ finding: "Covers or deadfront missing", priority: "Medium" });
  if (c.dust === true) out.push({ finding: "Contamination", priority: "Medium" });
  if (c.tamper === "broken") out.push({ finding: "Tamper sticker broken", priority: "Medium" });
  if (c.doors === false) out.push({ finding: "Door not latching", priority: "Low" });
  return out;
}

export function priorityOf(lines: readonly RequestLine[]): Priority {
  return lines.reduce<Priority>((top, l) => (PRIORITY_RANK[l.priority] > PRIORITY_RANK[top] ? l.priority : top), "Low");
}

/** "Damage or burn marks, Covers or deadfront missing" */
export function findingsText(lines: readonly RequestLine[]): string {
  return lines.map((l) => l.finding).join(", ");
}

/** The Work Started email's Findings row: "WR-0014 · High · Damage or burn marks, Covers or deadfront missing". */
export function findingsRowOf(requestId: string, lines: readonly RequestLine[]): string {
  return `${requestId} · ${priorityOf(lines)} · ${findingsText(lines)}`;
}

// ---- The record ----

export interface WorkRequestRow {
  requestId: string;
  /** jsp: written at JSP Submit. guest: Report a Problem on the guest page (piece 4b). */
  source: "jsp" | "guest";
  /** Guest reports: the one line the guest wrote. */
  note: string;
  siteNumber: string;
  assetId: string;
  location: string;
  equipment: string;
  jspNumber: string;
  qewId: string;
  holderName: string;
  priority: Priority;
  lines: RequestLine[];
  status: RequestStatus;
  workOrderNumber: string;
  workOrderSystem: string;
  closedHow: ClosedHow | null;
  resolution: string;
  closedByName: string;
  closedOn: string | null;
  serviceabilityAtReport: string;
  leftWorseNote: string;
  createdOn: string;
  createdAt: string;
  /** Made from a near miss (piece 6): the OBS number. Linked both ways. */
  observationId?: string;
}

export interface NewRequestInput {
  requestId: string;
  siteNumber: string;
  assetId: string;
  location: string;
  equipment: string;
  jspNumber: string;
  qewId: string;
  holderName: string;
  lines: RequestLine[];
  serviceabilityAtReport: string;
  today: string;
  nowIso: string;
}

export function newRequestOf(i: NewRequestInput): WorkRequestRow {
  return {
    requestId: i.requestId,
    source: "jsp",
    note: "",
    siteNumber: i.siteNumber,
    assetId: i.assetId,
    location: i.location,
    equipment: i.equipment,
    jspNumber: i.jspNumber,
    qewId: i.qewId,
    holderName: i.holderName,
    priority: priorityOf(i.lines),
    lines: i.lines,
    status: "Open",
    workOrderNumber: "",
    workOrderSystem: "",
    closedHow: null,
    resolution: "",
    closedByName: "",
    closedOn: null,
    serviceabilityAtReport: i.serviceabilityAtReport,
    leftWorseNote: "",
    createdOn: i.today,
    createdAt: i.nowIso,
  };
}

const str = (v: unknown) => (typeof v === "string" ? v : "");
const isPriority = (v: unknown): v is Priority => v === "High" || v === "Medium" || v === "Low";

export function requestRowOf(id: string, d: Record<string, unknown>): WorkRequestRow {
  const lines = Array.isArray(d.lines)
    ? (d.lines as Record<string, unknown>[]).filter((l) => l && isPriority(l.priority)).map((l) => ({ finding: str(l.finding), priority: l.priority as Priority }))
    : [];
  const status = d.status === "In Work" || d.status === "Closed" ? d.status : "Open";
  const how = d.closedHow === "Fixed On JSP" || d.closedHow === "Closed" || d.closedHow === "Not Needed" ? d.closedHow : null;
  return {
    requestId: id,
    source: d.source === "guest" ? "guest" : "jsp",
    note: str(d.note),
    siteNumber: str(d.siteNumber),
    assetId: str(d.assetId),
    location: str(d.location),
    equipment: str(d.equipment),
    jspNumber: str(d.jspNumber),
    qewId: str(d.qewId),
    holderName: str(d.holderName),
    priority: isPriority(d.priority) ? d.priority : priorityOf(lines),
    lines,
    status,
    workOrderNumber: str(d.workOrderNumber),
    workOrderSystem: str(d.workOrderSystem),
    closedHow: how,
    resolution: str(d.resolution),
    closedByName: str(d.closedByName),
    closedOn: typeof d.closedOn === "string" && d.closedOn ? d.closedOn : null,
    serviceabilityAtReport: str(d.serviceabilityAtReport),
    leftWorseNote: str(d.leftWorseNote),
    createdOn: str(d.createdOn),
    createdAt: str(d.createdAt),
    ...(str(d.observationId) ? { observationId: str(d.observationId) } : {}),
  };
}

// ---- The JSP close (section 3) ----

/** What the close pill does to the JSP's request. Null: leave it as it is. */
export function closeUpdateOf(
  r: Pick<WorkRequestRow, "status">,
  leftAs: string,
  note: string,
  who: { badgeId: string; holderName: string },
  today: string,
  nowIso: string,
): Record<string, unknown> | null {
  if (r.status === "Closed") return null;
  if (leftAs === "fixed")
    return {
      status: "Closed",
      closedHow: "Fixed On JSP",
      resolution: "Fixed by the QEW on the JSP.",
      closedBy: who.badgeId,
      closedByName: who.holderName,
      closedOn: today,
      closedAt: nowIso,
    };
  if (leftAs === "worse" && note.trim()) return { leftWorseNote: note.trim().slice(0, 1000) };
  return null;
}

// ---- Screening by the ESP Manager (section 4) ----

export type ScreenAction = "sentToCmms" | "close" | "notNeeded";

export interface ScreenInput {
  action: ScreenAction;
  workOrderNumber?: string;
  workOrderSystem?: string;
  resolution?: string;
  reason?: string;
  /** Set at screening. A guest report starts from its picks; the ESP Manager decides. */
  priority?: string;
}

export function screenProblem(r: Pick<WorkRequestRow, "status">, i: ScreenInput): string | null {
  if (r.status === "Closed") return "This request is already closed.";
  if (i.action === "sentToCmms") {
    if (!(i.workOrderNumber ?? "").trim()) return "Enter the work order number.";
    if (!(WORK_ORDER_SYSTEMS as readonly string[]).includes(i.workOrderSystem ?? "")) return "Pick the CMMS.";
    return null;
  }
  if (i.action === "close") return (i.resolution ?? "").trim() ? null : "Write a short resolution.";
  if (i.action === "notNeeded") return (i.reason ?? "").trim() ? null : "Give the reason it is not needed.";
  return "Which action?";
}

export function screenFieldsOf(i: ScreenInput, who: { badgeId: string; holderName: string }, today: string, nowIso: string): Record<string, unknown> {
  const stamp: Record<string, unknown> = { screenedBy: who.badgeId, screenedByName: who.holderName, screenedOn: today, screenedAt: nowIso };
  if (isPriority(i.priority)) stamp.priority = i.priority;
  if (i.action === "sentToCmms")
    return { ...stamp, status: "In Work", workOrderNumber: (i.workOrderNumber ?? "").trim().slice(0, 40), workOrderSystem: i.workOrderSystem };
  const closed = { closedBy: who.badgeId, closedByName: who.holderName, closedOn: today, closedAt: nowIso };
  if (i.action === "close") return { ...stamp, ...closed, status: "Closed", closedHow: "Closed", resolution: (i.resolution ?? "").trim().slice(0, 1000) };
  return { ...stamp, ...closed, status: "Closed", closedHow: "Not Needed", resolution: (i.reason ?? "").trim().slice(0, 1000) };
}

// ---- The list ----

/** Site first (or All Sites), Open or All. Open ones first, High at the top, then newest. */
export function requestRowsFor(all: readonly WorkRequestRow[], site: string, openOnly: boolean): WorkRequestRow[] {
  return all
    .filter((r) => (site === "all" || r.siteNumber === site) && (!openOnly || r.status !== "Closed"))
    .sort((a, b) => {
      const ao = a.status === "Closed" ? 0 : 1;
      const bo = b.status === "Closed" ? 0 : 1;
      if (ao !== bo) return bo - ao;
      if (ao && PRIORITY_RANK[a.priority] !== PRIORITY_RANK[b.priority]) return PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority];
      return b.createdAt.localeCompare(a.createdAt);
    });
}

/** Admin Home tile: the amber count is every Open request. In Work is with the CMMS. */
export function requestTileOf(all: readonly WorkRequestRow[]): { line: string; alert: number } {
  const open = all.filter((r) => r.status === "Open");
  const high = open.filter((r) => r.priority === "High").length;
  const inWork = all.filter((r) => r.status === "In Work").length;
  if (!open.length && !inWork) return { line: "None open", alert: 0 };
  const parts = [`${open.length} open`];
  if (high) parts.push(`${high} High`);
  if (inWork) parts.push(`${inWork} in work`);
  return { line: parts.join(" · "), alert: open.length };
}

/** The QEW page line at the asset. The newest request still open, if any. */
export function openRequestLineOf(rows: readonly WorkRequestRow[]): string | null {
  const r = [...rows].filter((x) => x.status !== "Closed").sort((a, b) => PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority] || b.createdAt.localeCompare(a.createdAt))[0];
  if (!r) return null;
  const [, m, d] = r.createdOn.split("-");
  const when = m && d ? ` Reported ${m}/${d}` : "";
  return `Open work request ${r.requestId} · ${r.priority}. ${findingsText(r.lines)}.${when}${r.jspNumber ? ` on JSP #${r.jspNumber.replace(/^JSP-/, "")}` : ""}.`;
}
